import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, flatMap, finalize, catchError, tap } from 'rxjs/operators';

import { User } from 'app/models/user';
import { Globals } from 'app/services/globals'
import * as _ from "lodash";
import { Team } from 'app/models/team';
import { Tag } from 'app/models/tag';
import moment from 'moment';

@Injectable({ providedIn: 'root' })
export class UserService {
    private currentUserSubject: BehaviorSubject<User>;
    public currentUser: Observable<User>;
    public fqdn: string;
    public access_token: string;
    public access_token_expires_at: number;

    private _cachedUsers: User[] = null;

    public get cachedUsers() : User[] {
        if (!this._cachedUsers) {
            this._cachedUsers = [];
            this.getAll().subscribe();
        }
        return this._cachedUsers;
    }
    
    constructor(private http: HttpClient, private globals: Globals) {
        this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(localStorage.getItem('currentUser')));
        this.currentUser = this.currentUserSubject.asObservable();

        // reset optimized permissions as they could have been changed
        this.currentUser.subscribe((u) => {if (u) u.optimizedPermissions = {}; });

        if (localStorage.getItem('fqdn') != null) {
            this.fqdn = localStorage.getItem('fqdn');
            this.globals.api_base_url = this.globals.api_url_scheme + this.fqdn + this.globals.api_suffix_path;
        }
        if (localStorage.getItem('access_token') != null) {
            this.access_token = localStorage.getItem('access_token');
        }
        if (localStorage.getItem('access_token_expires_at') != null) {
            this.access_token_expires_at = +localStorage.getItem('access_token_expires_at');
        }

        window.addEventListener('storage', function (e) {
            if (e.key == 'currentUser') {
                localStorage.removeItem('lockedScreen');
                if (window.location.pathname != '/login')
                    location.reload();
            }
        });

        this.currentUser.subscribe(user => {
            if (user && this.globals.api_base_url) {
                // get out of the constructors sequence
                setTimeout(() => {
                    this.getAll().subscribe();
                }, 1);
            }
        });
    }

    public get currentUserValue(): User {
        return this.currentUserSubject.value;
    }
    /**
     * verifyMail
     */

    public sendVerificationMail()  {
        return this.http.get<Observable<HttpResponse<any>>>(this.globals.api_base_url + `/email/resend`, {observe: 'response'});
    }
    public verifyMail(url: string)  {
        return this.http.get<Observable<HttpResponse<Object>>>(url ,{observe: 'response'});
    }

    public setToken(access_token: string, fqdn: string, clearClipboard: boolean = true, expires_after_seconds: number = 1000) {
        this.access_token = access_token;
        this.access_token_expires_at = Date.now() + expires_after_seconds * 1000;
        localStorage.setItem('access_token', this.access_token);
        localStorage.setItem('access_token_expires_at', this.access_token_expires_at.toString());
        
        // clear storage clipboard
        if (clearClipboard) {
            localStorage.removeItem('filesClipboard');
            localStorage.removeItem('formsClipboard');
        }

        // store fqdn
        localStorage.setItem('fqdn', fqdn);
        this.fqdn = fqdn;

        this.globals.api_base_url = this.globals.api_url_scheme + fqdn + this.globals.api_suffix_path;

        return this.getCurrent().pipe(map(
            user => {
                return user;
            }));
    }
    private loginInternal(email: string, passwordOrToken: string, fqdn: string, code: string = '', isToken: boolean = false, clearClipboard: boolean = true) {       
        let data = {};
        data['email'] = email;
        if (isToken)
            data['token'] = passwordOrToken;
        else
            data['password'] = passwordOrToken;
        data['code'] = code;

        return this.http.post<any>(this.globals.api_url_scheme + fqdn + this.globals.api_suffix_path + `/login` + ((isToken) ? '/direct' : ''), data)
            .pipe(flatMap(response => {
                return this.setToken(response.access_token, fqdn, clearClipboard, response.expires_in);
            }));

    }
    public sendResetPasswordMail(fqdnToUse, email: string) {
        let mail: any = {};
        mail.email = email;
        return this.http.post<any>(this.globals.api_url_scheme + fqdnToUse + this.globals.api_suffix_path + `/send_password_reset`, mail);
    }

    public resetPassword(token, password, password_confirmation, email, fqdnToUse) {
        let resetobj: any = {};
        resetobj.token = token;
        resetobj.email = email;
        resetobj.password = password;
        resetobj.password_confirmation = password_confirmation;

        return this.http.post<any>(this.globals.api_url_scheme + fqdnToUse + this.globals.api_suffix_path + `/reset_password`, resetobj);
    }

    login(email: string, password: string, fqdn: string, code: string = '', clearClipboard: boolean = true) {
        return this.loginInternal(email, password, fqdn, code, false, clearClipboard);
    }

    loginWithToken(email: string, token: string, fqdn: string, clearClipboard: boolean = true) {
        return this.loginInternal(email, token, fqdn, '', true, clearClipboard);
    }
    refreshToken() {
        return this.http.post<any>(`${this.globals.api_base_url}/login/refresh`, {}).pipe(map(response => {
            this.access_token = response.access_token;
            this.access_token_expires_at = Date.now() + response.expires_in * 1000;
            localStorage.setItem('access_token', this.access_token);
            localStorage.setItem('access_token_expires_at', this.access_token_expires_at.toString());
            return true;
        }), catchError(() => {
            // refresh failed, logout
            this.logout();

            return of(false);
        }));
    }

    getCompanies(email: string) {
        return this.http.get<any>(this.globals.api_public_base_url + `/companies`, { params: { email } })
            .pipe(map(response => {
                return response;
            }));
    }

    logout() {
        if (this.access_token) {        
            this.http.post<any>(this.globals.api_base_url + `/logout`, {}).pipe(finalize(() => {
            })).subscribe();

            // we're not waiting for the response of logout, we've cleared our local user and just notifying the backend.
            // remove user from local storage to log user out
            this.fqdn = null;
            localStorage.removeItem('fqdn');
            localStorage.removeItem('currentUser');
            localStorage.removeItem('access_token');
            this.access_token = null;
            this.access_token_expires_at = null;
            if (window.location.pathname != '/login')
                location.reload();        
        }
    }

    getAuthorizeUrl(clientId: number, redirectUri: string, scope: string, responseType: string, state: string = '') {
        return `${this.globals.api_base_url}/oauth/authorize?client_id=${clientId.toString()}&redirect_uri=${redirectUri}&response_type=${responseType}&scope=${scope}&state=${state}&bearer=${this.access_token}`;
    }

    getAll() {
        return this._cachedUsers && this._cachedUsers.length > 0 ? 
            of(this._cachedUsers)
            :
            this.http.get<User[]>(`${this.globals.api_base_url}/users`).pipe(tap(u => this._cachedUsers = u), map(users => {
                return (users || []).flatMap(u => {
                    if (!u.name)
                        u.name = u.email;
                    return u;
                });
            }));
    }

    // getById(id: number) {
    //     return this.http.get(`${config.apiUrl}/users/${id}`);
    // }

    getCurrent() {
        return this.http.get<User>(`${this.globals.api_base_url}/user`).pipe(map(user => {
            // store user details in local storage to keep user logged in between page refreshes
            this.globals.pusher_key = user.pusher_key;
            localStorage.setItem('currentUser', JSON.stringify(user));
            this.currentUserSubject.next(user);
            return user;
        }));
    }

    getZendeskJWTToken() {
        return this.http.get<any>(`${this.globals.api_base_url}/user/support_sso_jwt`);
    }

    getMFAQRCodeUrl() {
        return this.http.get<any>(`${this.globals.api_base_url}/user/mfa_qrcode_url`);
    }

    activateMFA(code) {
        return this.http.post<any>(`${this.globals.api_base_url}/user/activate_mfa`, {code: code});
    }

    deactivateMFA() {
        return this.http.post<any>(`${this.globals.api_base_url}/user/deactivate_mfa`, {});
    }

    getAdmins() {
        return this.http.get<User[]>(`${this.globals.api_base_url}/admins`);
    }

    putAdmin(users: User[]) {
        return this.http.put(`${this.globals.api_base_url}/admin`, {users: users});
    }

    deleteAdmin(user: User) {
        return this.http.delete(`${this.globals.api_base_url}/admin/${user.id}`);
    }

    uploadAvatar(file: File) {
        let formData = new FormData();
        formData.append('file', file);

        return this.http.post<User>(`${this.globals.api_base_url}/user/avatar`, formData);
    }
    currentUserAvatarURL(): string {
        return `${this.globals.api_base_url}/user/avatar`;
    }
    removeUserAvatar() {
        return this.http.delete(`${this.globals.api_base_url}/user/avatar`);
    }

    startRegistration(email: string, password: string) {
        return this.http.post(`${this.globals.api_public_base_url}/register`, {
            email: email,
            password: password
        });
    }

    registrationStatus(registrationId: string) {
        if (registrationId == '123') { 
            let since = moment();
            since.minutes(since.minutes() - (since.minutes() % 5));
            since.seconds(0);
            since.milliseconds(0);
            console.log(since.toISOString());

            if ((moment().minutes() % 5) == 4) {
                return of({
                    "status": "ready",
                    "since": "2022-09-04T23:08:00.000000Z"
                });
            } else {
                return of({
                    "status": "cloud",
                    "since": since.toISOString()
                });
            }
        } else {
            return this.http.get<any>(`${this.globals.api_public_base_url}/registration_status`, {
                params: {
                    registration_id: registrationId
                }
            });
        }
    }

    completeRegistration(name: string, companyName: string) {
        return this.http.post(`${this.globals.api_base_url}/user/complete_registration`, {
            name: name,
            company_name: companyName
        });
    }

    deactivateUser(id: number) {
        return this.http.delete(`${this.globals.api_base_url}/user/${id}`).pipe(tap(() => {
            this._cachedUsers = [];
        }));;
    }

    restoreUser(id: number) {
        return this.http.post(`${this.globals.api_base_url}/user/${id}/restore`, {}).pipe(tap(() => {
            this._cachedUsers = [];
        }));;
    }

    add(users: User[]) {
        return this.http.post<User[]>(`${this.globals.api_base_url}/user`, users).pipe(tap(() => {
            this._cachedUsers = [];
        }));
    }

    update(user: User) {
        return this.http.put<User>(`${this.globals.api_base_url}/user/${user.id}`, user).pipe(tap(() => {
            this._cachedUsers = [];
        }));;
    }

    resendInvite(userId: number) {
        return this.http.post<any>(`${this.globals.api_base_url}/user/${userId}/resend_invite`, {}).pipe(tap(() => {
            this._cachedUsers = [];
        }));;
    }

    can(permissionName: string): boolean {

        let user: User = this.currentUserValue;
        
        if (!user || !user.all_permissions)
            return false;

            let permissionParts: string[] = permissionName.split('.');

        return user.all_permissions.findIndex((p) => {
            let concatParts: string = null;

            return permissionParts.findIndex((part) => {
                concatParts = (concatParts)? `${concatParts}.${part}`: part;

                if (p.name == concatParts)
                    return true;
                
                return false;
            }) != -1;
        }) != -1;
    }

    generateOptimizedPermissions(permissionName: string) {
        let user: User = this.currentUserValue;
        
        if (!user) {            
            return;
        }

        user.optimizedPermissions[permissionName] = {};

        if (!user.all_permissions) {
            return;
        }

        let permissionParts: string[] = permissionName.split('.');

        user.all_permissions.forEach((p) => {
            let concatParts: string = null;

            permissionParts.forEach((part) => {
                concatParts = (concatParts)? `${concatParts}.${part}`: part;

                if (p.name == concatParts) {
                    if (!p.applies_to || p.applies_to == 'all') {
                        if (!user.optimizedPermissions[permissionName]) {
                            user.optimizedPermissions[permissionName] = {all: true};    // create the whole permission with all
                        } else if (!user.optimizedPermissions[permissionName].all) {
                            user.optimizedPermissions[permissionName].all = true;   // create the 'all' section
                        }
                    } else if (p.applies_to == 'personal') {
                        if (!user.optimizedPermissions[permissionName]) {
                            user.optimizedPermissions[permissionName] = {personal: true};    // create the whole permission with personal
                        } else if (!user.optimizedPermissions[permissionName].personal) {
                            user.optimizedPermissions[permissionName].personal = true;   // create the 'personal' section
                        }
                    } else if (p.applies_to == 'tags') {
                        if (!user.optimizedPermissions[permissionName]) {
                            user.optimizedPermissions[permissionName] = {tags: p.target_tags};    // create the whole permission with the target tags
                        } else if (!user.optimizedPermissions[permissionName].tags) {
                            user.optimizedPermissions[permissionName].tags = p.target_tags;
                        } else {
                            user.optimizedPermissions[permissionName].tags = _.unionBy(user.optimizedPermissions[permissionName].tags, p.target_tags, 'id');  
                        }
                    } else if (p.applies_to == 'teams') {
                        if (!user.optimizedPermissions[permissionName]) {
                            user.optimizedPermissions[permissionName] = {teams: p.target_teams};    // create the whole permission with the target teams
                        } else if (!user.optimizedPermissions[permissionName].teams) {
                            user.optimizedPermissions[permissionName].teams = p.target_teams;
                        } else {
                            user.optimizedPermissions[permissionName].teams = _.unionBy(user.optimizedPermissions[permissionName].teams, p.target_teams, 'id');  
                        }
                    } else if (p.applies_to == 'paths') {
                        if (!user.optimizedPermissions[permissionName]) {
                            user.optimizedPermissions[permissionName] = {paths: p.target_paths.map(p => p.path)};    // create the whole permission with the target paths
                        } else if (!user.optimizedPermissions[permissionName].paths) {
                            user.optimizedPermissions[permissionName].paths = p.target_paths.map(p => p.path);
                        } else {
                            user.optimizedPermissions[permissionName].paths = _.union(user.optimizedPermissions[permissionName].paths, p.target_paths.map(p => p.path));  
                        }
                    } else if (p.applies_to == 'workflow_names') {
                        if (!user.optimizedPermissions[permissionName]) {
                            user.optimizedPermissions[permissionName] = {workflow_names: p.target_workflow_names.map(p => p.name)};    // create the whole permission with the target workflow_names
                        } else if (!user.optimizedPermissions[permissionName].workflow_names) {
                            user.optimizedPermissions[permissionName].workflow_names = p.target_workflow_names.map(p => p.name);
                        } else {
                            user.optimizedPermissions[permissionName].workflow_names = _.union(user.optimizedPermissions[permissionName].workflow_names, p.target_workflow_names.map(p => p.name));  
                        }
                    }
                }
                
            });
        });
    }

    getPermissions(permissionName: string) {
        if (!this.currentUserValue)
            return {};

        if (!this.currentUserValue.optimizedPermissions[permissionName]) {
            this.generateOptimizedPermissions(permissionName);
        }

        return this.currentUserValue.optimizedPermissions[permissionName];
    }

    canForTeams(permissionName: string, teams: Team[]) : boolean {
        let optimizedPermissions = this.getPermissions(permissionName);

        return optimizedPermissions.all || (optimizedPermissions.teams && _.intersectionBy(teams, optimizedPermissions.teams, 'id').length > 0);
    }

    canForTags(permissionName: string, tags: Tag[]) : boolean {
        let optimizedPermissions = this.getPermissions(permissionName);

        return optimizedPermissions.all || (optimizedPermissions.tags && _.intersectionBy(tags, optimizedPermissions.tags, 'id').length > 0);
    }

    canForPath(permissionName: string, path: string) : boolean {
        let optimizedPermissions = this.getPermissions(permissionName);

        path = path.replace(/\/$/, "");     // remove trailing slash (if root, then we are expecting an empty string)
        return optimizedPermissions.all || (optimizedPermissions.paths && (optimizedPermissions.paths as string[]).includes(path));
    }
}